import { LocalVideoTrack, Room } from "twilio-video";
import { useCallback, useEffect } from "react";
import {
  BACKGROUND_FILTER_VIDEO_CONSTRAINTS,
  DEFAULT_VIDEO_CONSTRAINTS,
  SELECTED_BACKGROUND_SETTINGS_KEY
} from "../../../constants";
import {
  GaussianBlurBackgroundProcessor,
  ImageFit,
  isSupported,
  VirtualBackgroundProcessor
} from "@twilio/video-processors";
import Abstract from "../../../images/Abstract.jpg";
import AbstractThumb from "../../../images/thumb/Abstract.jpg";
import BohoHome from "../../../images/BohoHome.jpg";
import BohoHomeThumb from "../../../images/thumb/BohoHome.jpg";
import Bookshelf from "../../../images/Bookshelf.jpg";
import BookshelfThumb from "../../../images/thumb/Bookshelf.jpg";
import CoffeeShop from "../../../images/CoffeeShop.jpg";
import CoffeeShopThumb from "../../../images/thumb/CoffeeShop.jpg";
import Contemporary from "../../../images/Contemporary.jpg";
import ContemporaryThumb from "../../../images/thumb/Contemporary.jpg";
import CozyHome from "../../../images/CozyHome.jpg";
import CozyHomeThumb from "../../../images/thumb/CozyHome.jpg";
import Desert from "../../../images/Desert.jpg";
import DesertThumb from "../../../images/thumb/Desert.jpg";
import Fishing from "../../../images/Fishing.jpg";
import FishingThumb from "../../../images/thumb/Fishing.jpg";
import Flower from "../../../images/Flower.jpg";
import FlowerThumb from "../../../images/thumb/Flower.jpg";
import Kitchen from "../../../images/Kitchen.jpg";
import KitchenThumb from "../../../images/thumb/Kitchen.jpg";
import ModernHome from "../../../images/ModernHome.jpg";
import ModernHomeThumb from "../../../images/thumb/ModernHome.jpg";
import Nature from "../../../images/Nature.jpg";
import NatureThumb from "../../../images/thumb/Nature.jpg";
import Ocean from "../../../images/Ocean.jpg";
import OceanThumb from "../../../images/thumb/Ocean.jpg";
import Patio from "../../../images/Patio.jpg";
import PatioThumb from "../../../images/thumb/Patio.jpg";
import Plant from "../../../images/Plant.jpg";
import PlantThumb from "../../../images/thumb/Plant.jpg";
import SanFrancisco from "../../../images/SanFrancisco.jpg";
import SanFranciscoThumb from "../../../images/thumb/SanFrancisco.jpg";
import { Thumbnail } from "../../BackgroundSelectionDialog/BackgroundThumbnail/BackgroundThumbnail";
import { useLocalStorageState } from "../../../hooks/useLocalStorageState/useLocalStorageState";

export interface BackgroundSettings {
  type: Thumbnail;
  index?: number;
}

const imageNames: string[] = [
  "Abstract",
  "Boho Home",
  "Bookshelf",
  "Coffee Shop",
  "Contemporary",
  "Cozy Home",
  "Desert",
  "Fishing",
  "Flower",
  "Kitchen",
  "Modern Home",
  "Nature",
  "Ocean",
  "Patio",
  "Plant",
  "San Francisco"
];

const images = [
  AbstractThumb,
  BohoHomeThumb,
  BookshelfThumb,
  CoffeeShopThumb,
  ContemporaryThumb,
  CozyHomeThumb,
  DesertThumb,
  FishingThumb,
  FlowerThumb,
  KitchenThumb,
  ModernHomeThumb,
  NatureThumb,
  OceanThumb,
  PatioThumb,
  PlantThumb,
  SanFranciscoThumb
];

const rawImagePaths = [
  Abstract,
  BohoHome,
  Bookshelf,
  CoffeeShop,
  Contemporary,
  CozyHome,
  Desert,
  Fishing,
  Flower,
  Kitchen,
  ModernHome,
  Nature,
  Ocean,
  Patio,
  Plant,
  SanFrancisco
];

const isDesktopChrome = /Chrome/.test(navigator.userAgent);
let imageElements = new Map();

const getImage = (index: number): Promise<HTMLImageElement> => {
  console.log(`getImage: Attempting to load image at index ${index}`);
  return new Promise((resolve, reject) => {
    if (imageElements.has(index)) {
      console.log(
        `getImage: Image at index ${index} already cached, returning from cache`
      );
      return resolve(imageElements.get(index));
    }
    const img = new Image();
    img.onload = () => {
      console.log(
        `getImage: Image at index ${index} loaded successfully from ${rawImagePaths[index]}`
      );
      imageElements.set(index, img);
      resolve(img);
    };
    img.onerror = err => {
      console.error(
        `getImage: Failed to load image at index ${index} from ${rawImagePaths[index]}`,
        err
      );
      reject(err);
    };
    console.log(
      `getImage: Setting source for image at index ${index} to ${rawImagePaths[index]}`
    );
    img.src = rawImagePaths[index];
  });
};

export const backgroundConfig = {
  imageNames,
  images
};

const virtualBackgroundAssets =
  process.env.REACT_APP_VIRTUAL_BACKGROUND_PATH || "/virtualbackground";
console.log("Virtual background assets path:", virtualBackgroundAssets);

let blurProcessor: GaussianBlurBackgroundProcessor;
let virtualBackgroundProcessor: VirtualBackgroundProcessor;

// Add a function to check if the virtual background assets are accessible
async function checkVirtualBackgroundAssets() {
  const testFile = `${virtualBackgroundAssets}/segm.wasm`;
  console.log("Checking for virtual background assets at:", testFile);
  try {
    const response = await fetch(testFile, { method: "HEAD" });
    if (response.ok) {
      console.log("Virtual background assets are accessible");
      return true;
    } else {
      console.error(
        `Virtual background assets check failed: ${response.status} ${response.statusText}`
      );
      return false;
    }
  } catch (err) {
    console.error("Error checking virtual background assets:", err);
    return false;
  }
}

// Add a function to check if each background image is loadable
async function checkBackgroundImages() {
  console.log("Checking background images...");
  for (let i = 0; i < rawImagePaths.length; i++) {
    const src = rawImagePaths[i];
    const img = new Image();
    try {
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = src;
      });
      console.log(
        `Background image ${i} (${imageNames[i]}) loaded successfully`
      );
    } catch (err) {
      console.error(
        `Failed to load background image ${i} (${imageNames[i]}) from ${src}`,
        err
      );
    }
  }
}

export default function useBackgroundSettings(
  videoTrack: LocalVideoTrack | undefined,
  room?: Room | null
) {
  const [backgroundSettings, setBackgroundSettings] = useLocalStorageState<
    BackgroundSettings
  >(SELECTED_BACKGROUND_SETTINGS_KEY, { type: "none", index: 0 });

  // Check if assets are accessible
  useEffect(() => {
    checkVirtualBackgroundAssets();
    checkBackgroundImages();
  }, []);

  const setCaptureConstraints = useCallback(async () => {
    const { mediaStreamTrack, processor } = videoTrack ?? {};
    const { type } = backgroundSettings;
    if (type === "none" && processor) {
      return mediaStreamTrack?.applyConstraints(
        DEFAULT_VIDEO_CONSTRAINTS as MediaTrackConstraints
      );
    } else if (type !== "none" && !processor) {
      return mediaStreamTrack?.applyConstraints(
        BACKGROUND_FILTER_VIDEO_CONSTRAINTS as MediaTrackConstraints
      );
    }
  }, [backgroundSettings, videoTrack]);

  const removeProcessor = useCallback(() => {
    if (!videoTrack) {
      console.log("removeProcessor: No video track available");
      return;
    }

    if (!videoTrack.processor) {
      console.log("removeProcessor: No processor to remove");
      return;
    }

    try {
      console.log("removeProcessor: Removing processor from video track");
      videoTrack.removeProcessor(videoTrack.processor);
      console.log("removeProcessor: Processor removed successfully");
    } catch (err) {
      console.error(
        "removeProcessor: Error removing processor from video track:",
        err
      );
    }
  }, [videoTrack]);

  const addProcessor = useCallback(
    (
      processor: GaussianBlurBackgroundProcessor | VirtualBackgroundProcessor
    ) => {
      if (!videoTrack) {
        console.log(
          "addProcessor: No video track available, cannot add processor"
        );
        return;
      }

      if (videoTrack.processor === processor) {
        console.log("addProcessor: Processor already added to video track");
        return;
      }

      try {
        console.log(
          `addProcessor: Removing existing processor before adding ${
            processor instanceof GaussianBlurBackgroundProcessor
              ? "blur"
              : "virtual background"
          } processor`
        );
        removeProcessor();

        console.log("addProcessor: Adding processor to video track");
        videoTrack.addProcessor(processor, {
          inputFrameBufferType: "video",
          outputFrameBufferContextType: "webgl2"
        });
        console.log("addProcessor: Processor added successfully");
      } catch (err) {
        console.error(
          "addProcessor: Error adding processor to video track:",
          err
        );
      }
    },
    [videoTrack, removeProcessor]
  );

  useEffect(() => {
    if (!isSupported) {
      return;
    }
    // make sure localParticipant has joined room before applying video processors
    // this ensures that the video processors are not applied on the LocalVideoPreview
    const handleProcessorChange = async () => {
      console.log("Background processor: Starting initialization");
      try {
        if (!blurProcessor) {
          console.log("Background processor: Creating blur processor");
          blurProcessor = new GaussianBlurBackgroundProcessor({
            assetsPath: virtualBackgroundAssets
            // Disable debounce only on desktop Chrome as other browsers either
            // do not support WebAssembly SIMD or they degrade performance.
          });
          console.log("Background processor: Loading blur model");
          await blurProcessor.loadModel();
          console.log("Background processor: Blur model loaded successfully");
        }
        if (!virtualBackgroundProcessor) {
          console.log(
            "Background processor: Creating virtual background processor"
          );
          try {
            const img = await getImage(0);
            console.log(
              "Background processor: Background image loaded successfully"
            );
            virtualBackgroundProcessor = new VirtualBackgroundProcessor({
              assetsPath: virtualBackgroundAssets,
              backgroundImage: img,
              // Disable debounce only on desktop Chrome as other browsers either
              // do not support WebAssembly SIMD or they degrade performance.
              fitType: ImageFit.Cover
            });
            console.log(
              "Background processor: Loading virtual background model"
            );
            await virtualBackgroundProcessor.loadModel();
            console.log(
              "Background processor: Virtual background model loaded successfully"
            );
          } catch (err) {
            console.error(
              "Background processor: Error loading background image",
              err
            );
          }
        }
        if (!room?.localParticipant) {
          console.log(
            "Background processor: No local participant yet, waiting"
          );
          return;
        }

        // Switch to 640x480 dimensions on browsers that do not support
        // WebAssembly SIMD to achieve optimum performance.
        const processor = blurProcessor || virtualBackgroundProcessor;
        // Check if SIMD is enabled if processor exists
        console.log("Background processor: Checking SIMD capability");
        // Don't directly access _isSimdEnabled as it's private
        const isSimdEnabled = processor
          ? Boolean((processor as any).isSimdSupported)
          : false;
        console.log("Background processor: SIMD enabled:", isSimdEnabled);

        if (processor && !isSimdEnabled) {
          console.log(
            "Background processor: Setting video constraints for non-SIMD browsers"
          );
          await setCaptureConstraints();
        }

        console.log(
          "Background processor: Applying background setting type:",
          backgroundSettings.type
        );
        if (backgroundSettings.type === "blur") {
          console.log("Background processor: Adding blur processor");
          addProcessor(blurProcessor);
        } else if (
          backgroundSettings.type === "image" &&
          typeof backgroundSettings.index === "number"
        ) {
          console.log(
            "Background processor: Adding virtual background with image index:",
            backgroundSettings.index
          );
          try {
            const img = await getImage(backgroundSettings.index);
            console.log(
              "Background processor: Background image loaded for index:",
              backgroundSettings.index
            );
            virtualBackgroundProcessor.backgroundImage = img;
            addProcessor(virtualBackgroundProcessor);
          } catch (err) {
            console.error(
              "Background processor: Error loading specific background image",
              err,
              backgroundSettings.index
            );
          }
        } else {
          console.log(
            "Background processor: Removing processor, using normal video"
          );
          removeProcessor();
        }
      } catch (err) {
        console.error(
          "Background processor: Unexpected error during initialization",
          err
        );
      }
    };
    handleProcessorChange();
  }, [
    backgroundSettings,
    videoTrack,
    room,
    addProcessor,
    removeProcessor,
    setCaptureConstraints
  ]);

  return [backgroundSettings, setBackgroundSettings] as const;
}
